import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';
import { PageType } from 'app-types';

import useTracking from 'hooks/analytics';

import CircleTile from 'components/common/tile/CircleTile';
import Grid from 'components/common/Grid';

import focusManager from 'support/focus/FocusManager';

import { pageSelected } from 'actions/pageView';

import { getContentRowMetadata } from 'support/analytics/analyticsHelper';

export interface CircleGridDataItem {
  id: string | number;
  displayName?: string;
  staticImageUrl?: string;
  staticActiveImageUrl?: string;
  lottieImageUrl?: string;
  tag?: {
    key: string;
    type: { name: string };
  };
}

interface CircleGridProps {
  displayTitle?: boolean;
  title?: string | null;
  data: CircleGridDataItem[];
  titleClassName?: string | null;
  rowPos?: number | null;
  focusId?: string | number | null;
  className?: string | null;
  numCols?: number | null;
  prevBuffer?: number | null;
  nextBuffer?: number | null;
  forwardedRef?: React.Ref<unknown> | null;
}

interface TrackContentEventParams {
  rowTitle?: string | null;
  rowPos?: number | null;
  rowCol?: number | null;
  tag?: string | null;
  pageType?: string | null;
}

const trackContentEvent = (
  trackEvent: (name: string, data: Record<string, unknown>) => void,
  { rowTitle, rowPos, rowCol, tag, pageType }: TrackContentEventParams,
) => {
  const eventData = {
    ...getContentRowMetadata(rowTitle, rowPos, rowCol),
    moduleAction: 'click',
    clickText: `${pageType} carousel - ${tag}`,
  };
  trackEvent(`${pageType} carousel interactions`, eventData);
};

const CircleGrid = ({
  displayTitle = false,
  title = null,
  data,
  titleClassName = null,
  rowPos = null,
  focusId: focusIdProp = null,
  className = null,
}: CircleGridProps) => {
  const history = useHistory() as MemoryHistory;
  const dispatch = useDispatch();
  const { trackEvent } = useTracking();
  const rows = data;

  const [focusId, setFocusId] = useState<string | number | null | undefined>(focusIdProp);

  useEffect(() => {
    if (focusIdProp) {
      setFocusId(focusIdProp);
    } else {
      setFocusId(focusManager.getNewUniqueId());
    }
  }, [focusIdProp]);

  const renderGridItem = useCallback(
    (item: CircleGridDataItem, index: number) => {
      const tag = item?.tag?.key;
      const pageType = item?.tag?.type.name;

      const onClick = () => {
        dispatch(pageSelected(tag, pageType as PageType));
        trackContentEvent(trackEvent, {
          rowTitle: title,
          rowPos,
          rowCol: index + 1,
          tag,
          pageType,
        });
        history.push({
          pathname: `/${pageType}/${tag}`,
        });
      };

      return (
        <CircleTile
          title={item?.displayName}
          focusId={`grid-item-${item?.id}`}
          item={item}
          key={item?.id}
          staticImageSrc={item?.staticImageUrl}
          staticActiveImage={item?.staticActiveImageUrl}
          lottieImageSrc={item?.lottieImageUrl}
          index={index}
          onClick={onClick}
        />
      );
    },
    [dispatch, history, rowPos, title, trackEvent],
  );

  return (
    <>
      {displayTitle && (
        <div className={classNames(titleClassName, 'font-weight-bold')}>
          {title}
        </div>
      )}
      <Grid
        className={className}
        focusId={focusId}
        numCols={6}
        data={rows || []}
        prevBuffer={rows.length}
        nextBuffer={rows.length}
        renderItem={renderGridItem}
      />
    </>
  );
};

export default React.memo(
  React.forwardRef<unknown, CircleGridProps>((props, ref) => (
    <CircleGrid {...props} forwardedRef={ref} />
  )),
);
